import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import KPICard from "../../components/shared/KPICard";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Users,
  Store,
  DollarSign,
  TrendingUp,
  Wallet,
  Clock,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { getAuthHeaders } = useAuth();
  const [overview, setOverview] = useState(null);
  const [dailyStats, setDailyStats] = useState({ earnings: [], merchants: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { ...getAuthHeaders(), "Content-Type": "application/json" };
      
      const [overviewRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/overview`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/stats/daily`, { credentials: "include", headers }),
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (statsRes.ok) setDailyStats(await statsRes.json());
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value || 0);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-muted-foreground text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-foreground font-medium">
              {entry.name}: {entry.dataKey.includes("earnings") || entry.dataKey.includes("fees")
                ? formatCurrency(entry.value)
                : formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout title="Admin Overview" subtitle="Program performance at a glance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Admin Overview" subtitle="Program performance at a glance">
      <div data-testid="admin-dashboard" className="space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
          <KPICard
            title="Total Affiliates"
            value={formatNumber(overview?.total_affiliates)}
            subtitle={`${formatNumber(overview?.active_affiliates)} active`}
            icon={Users}
            variant="blue"
          />
          <KPICard
            title="Total Merchants"
            value={formatNumber(overview?.total_merchants)}
            subtitle={`${formatNumber(overview?.active_merchants)} active`}
            icon={Store}
            variant="green"
          />
          <KPICard
            title="Total Volume"
            value={formatCurrency(overview?.total_volume)}
            subtitle={`${formatCurrency(overview?.total_fees)} in fees`}
            icon={TrendingUp}
            variant="purple"
          />
          <KPICard
            title="Admin Earnings"
            value={formatCurrency(overview?.total_admin_earnings)}
            icon={Wallet}
            variant="amber"
          />
        </div>

        {/* Secondary stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Affiliate Payouts</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">
              {formatCurrency(overview?.total_affiliate_paid)}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Pending Payouts</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">
              {overview?.pending_payouts || 0}
            </p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-xl p-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Net Revenue</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              {formatCurrency((overview?.total_fees || 0) - (overview?.total_affiliate_paid || 0))}
            </p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">Revenue Over Time</h3>
            <div className="h-64">
              {dailyStats.earnings.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyStats.earnings}>
                    <defs>
                      <linearGradient id="colorFees" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorAdmin" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="_id" stroke="#71717a" tick={{ fontSize: 12 }} />
                    <YAxis stroke="#71717a" tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="total_fees"
                      name="Total Fees"
                      stroke="#3B82F6"
                      fill="url(#colorFees)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="admin_earnings"
                      name="Admin Earnings"
                      stroke="#10B981"
                      fill="url(#colorAdmin)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No revenue data yet
                </div>
              )}
            </div>
          </div>

          {/* Merchants Chart */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">New Merchants</h3>
            <div className="h-64">
              {dailyStats.merchants.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyStats.merchants}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="_id" stroke="#71717a" tick={{ fontSize: 12 }} />
                    <YAxis stroke="#71717a" tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Merchants"
                      stroke="#8B5CF6"
                      strokeWidth={2}
                      dot={{ fill: "#8B5CF6", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  No merchant data yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
